import { config } from '../config';
import { read, TABLE } from '../data/db';
import { getBlob } from '../data/blobStore';
import { flagResolutions, hypotheses, profile } from '../data/repos';
import { stateAt } from '../domain/clock';
import { MATCH_STATE } from '../domain/machine';
import { toHypothesisRow } from '../domain/setup';
import type { PreMatchCandidate } from '../domain/setup';
import type { FlagResolutionPlan, ResolveFlagCommand } from '../domain/commands';
import type { MatchAnchors, ObservationRow, TemplateRow } from '../types';
import { dispatch } from './bus';
import type { DispatchResult } from './bus';
import { newId } from './ids';
import { describeAudio } from './recorder';
import type { AudioDescription } from './recorder';

/**
 * FLAG-3 / HT-4 — the **I/O half** of the halftime flags sheet (build unit U-13c).
 *
 * §1.2 forbids `ui/` from importing `data/`, so the queue, the audio captions and the raw blob for
 * playback all arrive through here, and every write leaves through `dispatch`. The *decisions* are
 * elsewhere: `domain/commands/flags.ts` owns the guards, `domain/setup.ts` owns the one hypothesis
 * constructor, and `repos/flagResolutions` owns the derived queue.
 *
 * ── The queue is not stored, and this module does not cache it ──────────────
 *
 * DIST-1's *"a resolved flag MUST NOT reappear"* is enforced by `listUnresolvedFlags` **deriving**
 * the queue — flags minus resolutions — rather than by a `resolved` column that could disagree with
 * the resolution rows. Every read below goes back to that function for the same reason: a list held
 * across a resolve is a list that can show a flag the user has already dealt with.
 */

/** One row of FLAG-3's list: the flag, and what can honestly be said about its audio (§8.2). */
export interface FlagRow {
  readonly observationId: string;
  /** SES-10 — halftime-created flags all carry `halfLengthMin`, which §10.1 labels `HT`. */
  readonly matchMinute: number;
  readonly createdAt: number;
  /** §8.2 — `available` is read from the blob row, never inferred from "a recording was started". */
  readonly audio: AudioDescription;
}

/** Everything the flags sheet needs, from one read plus the per-flag audio captions. */
export interface FlagsContext {
  /** DIST-1's queue, ordered `(matchMinute asc, createdAt asc)` per §10.1. */
  readonly unresolved: readonly FlagRow[];
  /** §10.1 (a) — the patterns a flag can be attached to instead of creating a new one. */
  readonly attachable: readonly AttachTarget[];
  /** §10.1 (b) — PRE-2's seeded library, which supplies real text *and* a `discoveryBucket`. */
  readonly library: readonly TemplateRow[];
  /**
   * §6.2 / DD-3 — whether the board has a free slot **right now**.
   *
   * Read here rather than counted by the sheet because the answer decides which of two behaviours
   * §7.4's *"optionally a board position"* takes: with a slot free, DD-3 says *"no sheet appears"*
   * and one more tap places the tile; with the board full, placing requires naming a tile to
   * displace, which is DD-3's chooser and lives in the HT-2 sheet. A sheet that counted tiles from
   * its own render would be a tap out of date on exactly the decision LOCK-6 says must never be
   * silent.
   */
  readonly boardHasFreeSlot: boolean;
}

/** One candidate for §10.1's *attach to an existing hypothesis* path. */
export interface AttachTarget {
  readonly id: string;
  readonly text: string;
  readonly onBoard: boolean;
}

/**
 * FLAG-3's queue plus everything the promote paths offer, in **one** readonly transaction.
 *
 * One transaction for the same reason `loadHalftimeContext` uses one: the queue, the attachable
 * patterns and the board's free-slot state describe one moment. A queue read before a promote and a
 * board state read after it would render a sheet whose "put it on the board" offer disagrees with
 * the board it is talking about.
 *
 * The **audio captions are read afterwards**, in their own transaction, and deliberately: they touch
 * `audioBlobs`, the one table nothing else in this read needs, and a blob row's byte column has no
 * business in the same scope as the queue derivation. Failure there degrades one caption (§8.2's
 * *"audio unavailable"*), never the queue.
 *
 * @throws {StorageError} on a read failure. Not swallowed: an empty queue and a failed read look
 *   identical to the user, and only one of them means there is nothing to review.
 */
export async function loadFlagsContext(matchId: string): Promise<FlagsContext> {
  const base = await read(
    [TABLE.observations, TABLE.flagResolutions, TABLE.hypotheses, TABLE.templates],
    async () => {
      const unresolved = await flagResolutions.listUnresolvedFlags(matchId);
      const rows = await hypotheses.listHypothesesForMatch(matchId);
      return {
        unresolved,
        attachable: rows.map((row) => ({
          id: row.id,
          text: row.text,
          onBoard: row.boardPosition !== null,
        })),
        boardHasFreeSlot: rows.filter((row) => row.boardPosition !== null).length < BOARD_SLOTS(),
        library: await profile.listTemplates(),
      };
    },
    'loadFlagsContext',
  );

  return {
    ...base,
    unresolved: await Promise.all(base.unresolved.map(withAudio)),
  };
}

/** §8.2's caption for one flag — *"audio unavailable — flagged at 34′"*, or nothing to say. */
async function withAudio(flag: ObservationRow): Promise<FlagRow> {
  return {
    observationId: flag.id,
    matchMinute: flag.matchMinute,
    createdAt: flag.createdAt,
    audio: await describeAudio(flag.audioBlobId, flag.matchMinute),
  };
}

/**
 * `BOARD_CAP`, read through `config` at call time rather than captured at module load.
 *
 * A function so the Zod-validated module is the single source (NFR-9) and nothing here holds a copy
 * that a config change would leave stale.
 */
function BOARD_SLOTS(): number {
  return config.BOARD_CAP;
}

/**
 * AUD-5 / FLAG-3 — the bytes for playback.
 *
 * Returns the `Blob` rather than an object URL, because the URL's **lifetime** belongs to whoever
 * mounts the `<audio>` element: `createObjectURL` without a matching `revokeObjectURL` leaks the
 * whole recording for the life of the document, and a leak decided in this module would be released
 * in a component that did not create it. `playability.ts` makes the same split for the same reason.
 *
 * `null` when there is nothing playable — no blob id, no row, finalize never ran, or zero bytes. The
 * caller has already been told which by {@link FlagRow.audio}; this one answers only *can it play*.
 *
 * @throws {StorageError} on a read failure.
 */
export async function readFlagAudio(audioBlobId: string | null): Promise<Blob | null> {
  if (audioBlobId === null) return null;
  const row = await read([TABLE.audioBlobs], () => getBlob(audioBlobId), 'readFlagAudio');
  return row?.complete === true && row.blob !== null && row.bytes > 0 ? row.blob : null;
}

// ── Resolving ────────────────────────────────────────────────────────────────

/** DIST-1 — *"Dismiss → resolution kind `dismissed`."* The flag Observation is untouched (INV-1). */
export function dismissFlag(matchId: string, observationId: string): Promise<DispatchResult> {
  return resolve(matchId, observationId, { kind: 'dismiss' });
}

/** §10.1 (a) — attach the flag to a pattern that already exists. Creates no row. */
export function attachFlag(
  matchId: string,
  observationId: string,
  hypothesisId: string,
): Promise<DispatchResult> {
  return resolve(matchId, observationId, { kind: 'attach', hypothesisId });
}

/**
 * §10.1 (b) and (c) — promote the flag into a **new** pattern.
 *
 * The caller builds the `PreMatchCandidate`, exactly as `addHalftimeHypothesis`'s callers do, and
 * for the same two reasons: `toHypothesisRow` stays U-7's **one** constructor (a second is a second
 * place that could assign a `boardPosition`), and the id is minted at the call site so the surface
 * knows which pattern it just created without re-deriving "the newest one" afterwards.
 *
 * Path (b) supplies a template's text and its `discoveryBucket`; path (c) supplies {@link autoLabel}
 * plus a tapped phase, from which §3.2's source-2 mapping derives the bucket. Both are taps alone,
 * which is §10.1's requirement.
 *
 * **Slots are not resolved on this path.** A `TemplateEntry` with a fill-in-the-blank needs PRE-4's
 * picker, which lives in the add sheet; the flags sheet offers only slot-free entries rather than
 * carrying an unresolved `{shirt}` into a pattern the user reads aloud at full time.
 */
export function promoteFlag(
  matchId: string,
  observationId: string,
  candidate: PreMatchCandidate,
  createdAt: number = Date.now(),
): Promise<DispatchResult> {
  return resolve(matchId, observationId, {
    kind: 'create',
    row: toHypothesisRow(matchId, candidate, createdAt),
  });
}

/**
 * §10.1's auto-label, in one place.
 *
 * The prime is the same minute mark every other surface uses, so a pattern named here reads like the
 * flag it came from rather than like a placeholder someone forgot to fill in.
 */
export function autoLabel(matchMinute: number): string {
  return `Flagged ${String(matchMinute)}′`;
}

/**
 * §10.1's queue label — *"`HT · 35′`"* or *"`35′`"* — derived from the **source state** (§2.4a).
 *
 * ── The obvious reading of §10.1's own sentence is wrong ────────────────────
 *
 * §10.1 first read *"halftime-created flags all carry `matchMinute = halfLengthMin` (SES-10) and are
 * labeled `HT` so they are distinguishable"*. Implemented literally that **mislabels**, and U-14a-i's
 * browser pass caught it: SES-4 derives a second-half minute as `halfLengthMin + floor(elapsed)`, so
 * a flag taken in the **first minute of the second half** carries `halfLengthMin` too and rendered
 * as `HT · 35′`. §2.4a generalises the defect — *a projected value is not a discriminator; once two
 * states project to one value the projection cannot be un-projected* — and names the recovery: the
 * anchors partition the wall-clock line, so `stateAt(anchors, createdAt)` returns the state the flag
 * was created in, exactly and by construction. This is the third of INV-7's four allowlisted
 * `createdAt` call sites.
 *
 * ── Why it is a pure function here and not a helper inside the component ────
 *
 * `vitest.config.ts` includes `src/**&#47;*.test.ts` only — there is no DOM test library in this
 * project — so a label computed inside a `.tsx` is assertable **only** in a browser pass. §17.1
 * states this as a unit-test row with a sharp negative (*a first-minute-of-2H flag renders WITHOUT
 * `HT`*), and a rule whose only witness is a WebKit walk is a rule that regresses between walks.
 * Both surfaces that render a flag queue call this one function.
 */
export function flagMinuteLabel(
  anchors: MatchAnchors,
  flag: { readonly matchMinute: number; readonly createdAt: number },
): string {
  return stateAt(anchors, flag.createdAt) === MATCH_STATE.halftime
    ? `HT · ${String(flag.matchMinute)}′`
    : `${String(flag.matchMinute)}′`;
}

function resolve(
  matchId: string,
  observationId: string,
  plan: FlagResolutionPlan,
): Promise<DispatchResult> {
  const command: ResolveFlagCommand = {
    type: 'ResolveFlag',
    matchId,
    observationId,
    // Minted at the call site, exactly as every other insert in this codebase is (§5.1's
    // discipline) — the bus mints no ids.
    resolutionId: newId(),
    plan,
  };
  return dispatch(command);
}
